<template>
  <div
    id="contact_details"
    class="flex flex-col gap-6 text-[13px] secondary_color proxima_nova"
  >
    <div id="first_name" class="flex flex-col gap-1">
      <span>First Name*</span>
      <a-input
        v-model:value="localFormData.firstName"
        class="input_medium"
        placeholder="Enter first name"
      />
    </div>

    <div id="last_name" class="flex flex-col gap-1">
      <span>Last Name*</span>
      <a-input
        v-model:value="localFormData.lastName"
        class="input_medium"
        placeholder="Enter last name"
      />
    </div>

    <div id="work_email" class="flex flex-col gap-1">
      <span>Work Email*</span>
      <CustomInput
        format="text"
        v-model="localFormData.workEmail"
        placeholder="Enter work email"
        :isError="emailError"
        errorMessage="Please enter a valid email address"
        type="primary"
        :onChange="handleEmailChange"
      />
    </div>

    <div id="phone_number" class="flex flex-col gap-1">
      <span>Phone Number*</span>
      <vue-tel-input
        v-model="localFormData.phoneNumber"
        :defaultCountry="defaultCountryCode"
        :validCharactersOnly="true"
        :onlyNumbers="true"
        :dropdownOptions="{
          showFlags: true,
          showDialCodeInList: true,
          showSearchBox: true,
          searchBoxPlaceholder: 'Search country..',
        }"
        mode="international"
      />
    </div>

    <a-button
      class="primary_medium self-end"
      :disabled="!isStepValid"
      :loading="submitting"
      @click="handleContinue"
    >
      {{ submitting ? "Submitting" : "Submit" }}
    </a-button>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { VueTelInput } from "vue-tel-input";
import { capitalizeFirstLetter, validateEmail } from "@/src/utils/utils.js";
import CallService from "@/src/api/CallService.js";
import CustomInput from "@/src/components/CustomInput.vue";

const store = useStore();
const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);

onMounted(() => {
  store.dispatch("workspace/fetchDefaultCountryCode");
});

const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update:formData", "next-step"]);

const router = useRouter();

const localFormData = ref({ ...props.formData });
const submitting = ref(false);
const emailError = ref(false);

const isStepValid = computed(() => {
  return Boolean(
    localFormData.value.firstName &&
      localFormData.value.lastName &&
      localFormData.value.workEmail &&
      localFormData.value.phoneNumber,
  );
});

const handleEmailChange = () => {
  try {
    emailError.value = false;
  } catch (error) {
    console.error("An error occurred while handling email change: ", error);
  }
};

const validateForm = () => {
  try {
    let isValid = true;

    if (!localFormData.value.firstName || !localFormData.value.lastName) {
      isValid = false;
    }

    if (
      localFormData.value.workEmail &&
      !validateEmail(localFormData.value.workEmail)
    ) {
      emailError.value = true;
      isValid = false;
    } else {
      emailError.value = false;
    }

    return isValid;
  } catch (error) {
    console.error("An error occurred while validating form: ", error);
  }
};

const toCreateRegulatoryBundle = async () => {
  try {
    submitting.value = true;

    const payload = {
      id: localFormData.value?.infoID,
      first_name: localFormData.value?.firstName,
      last_name: localFormData.value?.lastName,
      phone_number: localFormData.value?.phoneNumber?.replace(/\s+/g, "") || "",
      email_id: localFormData.value?.workEmail || "",
      country_code: localFormData.value?.countryCode || "",
    };

    const response = await CallService.createRegulatoryBundle(payload);
    if (response?.success) {
      message.success("Business details submitted successfully. Approval will take 2-3 days!");
      await router.push("/settings#phone_numbers");
      return true;
    }

    return false;
  } catch (error) {
    console.error(
      "An error occurred while creating regulatory bundle: ",
      error,
    );
    message.error(
      capitalizeFirstLetter(
        error?.response?.data?.error || "Failed to submit contact details",
      ),
    );
    return false;
  } finally {
    submitting.value = false;
  }
};

const handleContinue = async () => {
  try {
    if (!validateForm()) return;

    emit("update:formData", { ...localFormData.value });

    const success = await toCreateRegulatoryBundle();
    if (success) {
      emit("next-step");
    }
  } catch (error) {
    console.error("An error occurred while handling continue: ", error);
  }
};

watch(
  () => props.formData,
  (newVal) => {
    localFormData.value = { ...newVal };
  },
  { deep: true },
);
</script>
